import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UserOnboarding {
  id: string;
  user_id: string;
  has_completed_onboarding: boolean;
  has_selected_plan: boolean;
  has_accepted_terms: boolean;
  has_created_first_account: boolean;
  has_completed_tour: boolean;
  has_seen_upgrade_modal: boolean;
  has_dismissed_upgrade_banner: boolean;
  upgrade_banner_dismissed_at: string | null;
  onboarding_completed_at: string | null;
  created_at: string;
  updated_at: string;
}

type OnboardingBooleanField =
  | "has_completed_onboarding"
  | "has_selected_plan"
  | "has_accepted_terms"
  | "has_created_first_account"
  | "has_completed_tour"
  | "has_seen_upgrade_modal"
  | "has_dismissed_upgrade_banner";

export function useOnboarding() {
  const queryClient = useQueryClient();

  const { data: onboarding, isLoading, error } = useQuery<UserOnboarding | null>({
    queryKey: ["user-onboarding"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("user_onboarding" as any)
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      // User registered before the subscription migration — no row in user_onboarding.
      // Treat them as having completed onboarding so they go directly to dashboard.
      if (!data) {
        return { has_completed_onboarding: true } as unknown as UserOnboarding;
      }

      return data as unknown as UserOnboarding;
    },
    staleTime: 2 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: async (fields: Partial<UserOnboarding>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { data, error } = await supabase
        .from("user_onboarding" as any)
        .update(fields as any)
        .eq("user_id", user.id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as UserOnboarding;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-onboarding"] });
    },
  });

  const completeStep = async (field: OnboardingBooleanField) => {
    const updatePayload: Partial<UserOnboarding> = { [field]: true };

    if (field === "has_completed_onboarding") {
      updatePayload.onboarding_completed_at = new Date().toISOString();
    }

    return updateMutation.mutateAsync(updatePayload);
  };

  return {
    onboarding,
    isLoading,
    error,
    updateOnboarding: updateMutation.mutateAsync,
    completeStep,
    isUpdating: updateMutation.isPending,
  };
}
